/**
 * 操作记录 - 详情页右侧 sticky 时间线
 *
 * UI 对齐 web后台.html OP_LOG：
 *  - 每个节点：彩色圆点 + 标题 + 用户头像 + 时间 + 状态/备注/操作 三段
 *  - 颜色映射按 action 类型
 */
import React from 'react'
import { OperationLogItem } from '../api/check'
import { ACTION_LABEL } from '../constants/check'

const ACTION_COLORS: Record<string, string> = {
  create: '#3B7DEB',
  ai_analysis: '#7B5BEB',
  submit_rectify: '#FF8B3D',
  submit_appeal: '#7B5BEB',
  review_pass: '#10A66B',
  review_reject: '#E84C4C',
  appeal_pass: '#10A66B',
  appeal_reject: '#E84C4C'
}

interface OperationLogProps {
  logs: OperationLogItem[]
}

/**
 * 从 operation_log.detail 字段中提取 备注/操作 文案。
 *
 * 后端约定(检查项记录管理.md 5.3,2026-05-20 更新):
 *   detail 解析后含可选 key:
 *     - remark:        用户操作时填写的备注;未填则不写入此 key,前端整行不展示
 *     - media_count:   submit_rectify 本次照片总数
 *     - added_count:   submit_rectify 相对上一轮新增数(>0 才写)
 *     - removed_count: submit_rectify 相对上一轮删除数(>0 才写)
 */
function pickRemark(detail: any): string | null {
  if (!detail || typeof detail !== 'object') return null
  const v = detail.remark
  return typeof v === 'string' && v.trim() ? v : null
}

function pickOperation(detail: any, action: string): string | null {
  if (!detail || typeof detail !== 'object') return null
  if (action !== 'submit_rectify') return null
  const added = typeof detail.added_count === 'number' ? detail.added_count : 0
  const removed =
    typeof detail.removed_count === 'number' ? detail.removed_count : 0
  const total =
    typeof detail.media_count === 'number' ? detail.media_count : null
  const parts: string[] = []
  if (added > 0) parts.push(`新增 ${added} 张`)
  if (removed > 0) parts.push(`删除 ${removed} 张`)
  if (parts.length === 0) {
    return total !== null ? `共 ${total} 张` : null
  }
  return total !== null
    ? `${parts.join('、')}(共 ${total} 张)`
    : parts.join('、')
}

const UserAvatar: React.FC<{ name: string }> = ({ name }) => {
  const ch = (name || '?').charAt(0).toUpperCase()
  return <span className="myse-avatar myse-avatar--sm">{ch}</span>
}

const OperationLog: React.FC<OperationLogProps> = ({ logs }) => {
  if (!logs || logs.length === 0) {
    return (
      <div
        style={{
          padding: '24px 0',
          color: '#9CA3AF',
          fontSize: 13,
          textAlign: 'center'
        }}
      >
        暂无操作记录
      </div>
    )
  }

  return (
    <div className="myse-timeline">
      {logs.map((log, i) => {
        const color = ACTION_COLORS[log.action] || '#9CA3AF'
        const last = i === logs.length - 1
        const title = log.action_label || ACTION_LABEL[log.action] || log.action
        const user = log.operator_name || log.operator_id || '系统'
        const remark = pickRemark(log.detail)
        const operation = pickOperation(log.detail, log.action)
        const hasBody = remark !== null || operation !== null
        return (
          <div
            key={log.log_id}
            className={
              'myse-timeline__node' + (last ? ' myse-timeline__node--last' : '')
            }
          >
            <div className="myse-timeline__dot-wrap">
              <span
                className="myse-timeline__dot"
                style={{
                  background: color,
                  boxShadow: `0 0 0 3px ${color}22`
                }}
              />
              {!last && <span className="myse-timeline__line" />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="myse-timeline__title">{title}</div>
              <div className="myse-timeline__meta">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <UserAvatar name={user} />
                  {user}
                </span>
                <span
                  style={{ color: '#9CA3AF', fontFamily: 'Inter, system-ui' }}
                >
                  {log.create_time}
                </span>
              </div>
              {hasBody && (
                <div className="myse-timeline__body">
                  {remark !== null && (
                    <div>
                      <span style={{ color: '#9CA3AF' }}>备注：</span>
                      <span
                        style={{
                          color: '#374151',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }}
                      >
                        {remark}
                      </span>
                    </div>
                  )}
                  {operation !== null && (
                    <div>
                      <span style={{ color: '#9CA3AF' }}>操作：</span>
                      <span style={{ color: '#3B7DEB' }}>{operation}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default OperationLog
